import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MovieCard from "../components/MovieCard";
import Sidebar from "../components/layout/Sidebar";
import PageHeader from "../components/PageHeader";
import { getUserInfo } from "../services/api";

export default function GroupRecommendations() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const groupName = location.state?.groupName || "Group";

  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [headerOffset, setHeaderOffset] = useState('132.5px');
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchGroupRecommendations = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/groups/api/${groupId}/recommendations/`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.Titles && Array.isArray(data.Titles)) {
          setMovies(data.Titles);
        } else {
          setMovies([]);
        }
        
        setError(null);
      } catch (err) {
        console.error("Error fetching group recommendations:", err);
        setError("Failed to load group recommendations");
        setMovies([]);
      } finally {
        setLoading(false);
      }
    };

    if (groupId) {
      fetchGroupRecommendations();
    }
  }, [groupId]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await getUserInfo();
        setUser(userData);
      } catch (e) {
        console.error('Error fetching user info:', e);
      }
    };
    fetchUser();
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Measure header height and set offset for mid breakpoint
  useEffect(() => {
    const updateHeaderOffset = () => {
      const headerContent = document.querySelector('.page-header .header-content');
      if (!headerContent) return;
      const isSingleAction = headerContent.classList.contains('single-action');
      setHeaderOffset(isSingleAction ? '72.5px' : '132.5px');
    };
    requestAnimationFrame(updateHeaderOffset);
    window.addEventListener('resize', updateHeaderOffset);
    return () => window.removeEventListener('resize', updateHeaderOffset);
  }, []);

  const handlePreferenceChange = async (imdbId, preference) => {
    // Handle group preference changes
    try {
      const response = await fetch("/groups/vote/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCookie("csrftoken"),
        },
        body: JSON.stringify({
          imdb_id: imdbId,
          groupname: groupName,
          preference: preference,
        }),
      });

      if (response.ok) {
        // Update the movie's preference in the local state
        setMovies(prevMovies =>
          prevMovies.map(movie =>
            movie.imdb_id === imdbId
              ? { ...movie, user_preference: preference }
              : movie
          )
        );
      }
    } catch (error) {
      console.error("Error updating preference:", error);
    }
  };

  // Helper function to get CSRF token
  function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== "") {
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.startsWith(name + "=")) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  return (
    <>
      <div className="groups-page-context" style={{ ['--groups-header-offset']: headerOffset }}>
        <Sidebar user={user} />
        <div className="main-sec">
          <div className="home-container">
            <PageHeader 
              title={`Recommendations for ${groupName}`}
              actions={[
                {
                  label: "← Back to Groups",
                  onClick: () => navigate("/groups"),
                  className: "btn btn-secondary"
                }
              ]}
            />
            {loading ? (
              <div className="loading-state">
                <h2>Loading group recommendations...</h2>
                <p>This may take a moment while we calculate recommendations for your group.</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <h2>Error loading recommendations</h2>
                <p>{error}</p>
                <button 
                  className="btn btn-primary" 
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </button>
              </div>
            ) : movies.length > 0 ? (
              <div className="movies-grid">
                {movies.map(movie => (
                  <MovieCard
                    key={movie.imdb_id}
                    movie={movie}
                    onPreferenceChange={handlePreferenceChange}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <h2>No group recommendations available</h2>
                <p>
                  Group recommendations are calculated based on the individual preferences of group members. 
                  To get group recommendations:
                </p>
                <ul style={{ textAlign: 'left', maxWidth: '500px', margin: '20px auto' }}>
                  <li>Group members need to have individual recommendations first</li>
                  <li>Members should rate some movies/shows on their personal recommendations page</li>
                  <li>Once members have preferences, group recommendations will appear here</li>
                </ul>
                <button 
                  className="btn btn-primary" 
                  onClick={() => navigate("/")}
                >
                  Go to My Recommendations
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
